import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { CreditCard, Lock, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';

// Initialize Stripe with publishable key from environment
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Card element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: '"Inter", system-ui, sans-serif',
      '::placeholder': {
        color: '#6b7280',
      },
      iconColor: '#a855f7',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// The checkout form component
const CheckoutForm = ({ 
  clientSecret, 
  tierName, 
  amount, 
  billingPeriod,
  onSuccess, 
  onCancel,
  onError 
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [succeeded, setSucceeded] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        onError?.(stripeError);
      } else if (paymentIntent.status === 'succeeded') {
        setSucceeded(true);
        onSuccess?.(paymentIntent);
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      onError?.(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  if (succeeded) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 text-green-400 mb-4">
          <CheckCircle className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Payment Successful!</h3>
        <p className="text-gray-400 mb-6">
          Welcome to {tierName}! Your subscription is now active.
        </p>
        <button
          onClick={onSuccess}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-semibold transition-colors"
        >
          Get Started
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Order Summary */}
      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-400">Plan</span>
          <span className="text-white font-semibold">{tierName}</span>
        </div>
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-400">Billing</span>
          <span className="text-white capitalize">{billingPeriod}</span>
        </div>
        <div className="border-t border-gray-700 my-3" />
        <div className="flex justify-between items-center">
          <span className="text-gray-400">Total</span>
          <span className="text-2xl font-bold text-white">
            ${(amount / 100).toFixed(2)}
            <span className="text-sm text-gray-400 font-normal">
              /{billingPeriod === 'yearly' ? 'year' : 'month'}
            </span>
          </span>
        </div>
      </div>

      {/* Card Input */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          <CreditCard className="w-4 h-4 inline mr-2" />
          Card Details
        </label>
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 focus-within:border-purple-500 transition-colors">
          <CardElement 
            options={cardElementOptions} 
            onChange={handleCardChange}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Security Notice */}
      <div className="flex items-center gap-2 text-gray-500 text-xs">
        <Lock className="w-3 h-3" />
        <span>Your payment is secured with 256-bit SSL encryption</span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-semibold transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing || !cardComplete}
          className="flex-1 px-4 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              Pay ${(amount / 100).toFixed(2)}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Main Modal Component
const StripeCheckoutModal = ({
  isOpen,
  onClose,
  clientSecret,
  tierName,
  amount,
  billingPeriod,
  onSuccess
}) => {
  if (!isOpen) return null;

  const handleSuccess = (paymentIntent) => {
    onSuccess?.(paymentIntent);
    onClose();
  };

  const handleError = (error) => {
    console.error('Payment error:', error);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-gray-900 rounded-2xl border border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Complete Your Purchase</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CheckoutForm
                clientSecret={clientSecret}
                tierName={tierName}
                amount={amount}
                billingPeriod={billingPeriod}
                onSuccess={handleSuccess}
                onCancel={onClose}
                onError={handleError}
              />
            </Elements>
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Simplified hook for using Stripe checkout
export const useStripeCheckout = () => {
  const [checkoutState, setCheckoutState] = useState({
    isOpen: false,
    clientSecret: null,
    tierName: '',
    amount: 0,
    billingPeriod: 'monthly'
  });

  const openCheckout = ({ clientSecret, tierName, amount, billingPeriod }) => {
    setCheckoutState({
      isOpen: true,
      clientSecret,
      tierName,
      amount,
      billingPeriod
    });
  };

  const closeCheckout = () => {
    setCheckoutState(prev => ({ ...prev, isOpen: false }));
  };

  return {
    checkoutState,
    openCheckout,
    closeCheckout
  };
};

export { StripeCheckoutModal, CheckoutForm };
export default StripeCheckoutModal;
